import React from "react";
import PropTypes from "prop-types";
import {ATooltip} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {TOOLTIP_OPEN_DELAY_DEFAULT, useTooltip} from "../hooks/useTooltip";
import css from "./TooltipDelay.module.scss";

const Tooltip = ({
  content,
  openDelay = TOOLTIP_OPEN_DELAY_DEFAULT,
  closeDelay = undefined, // "openDelay / 2" if not set
  children,
  placement = "top-left",
  title,
  subtitle,
  ...tooltipProps
}) => {
  const {
    anchorRef,
    isTooltipOpen,
    openTooltipWithDelay,
    closeTooltipWithDelay
  } = useTooltip({
    openDelay,
    closeDelay
  });

  const modifiedChild = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        onMouseEnter: openTooltipWithDelay,
        onMouseLeave: closeTooltipWithDelay,
        ref: anchorRef
      });
    }
    return child;
  });

  return (
    <>
      {modifiedChild}
      <ATooltip
        placement={placement}
        pointer
        open={isTooltipOpen}
        anchorRef={anchorRef}
        className={classNames(css.tooltipDelay, tooltipProps.className)}
        {...tooltipProps}>
        {title && <div className={css.title}>{title}</div>}
        {subtitle && <div className={css.subtitle}>{subtitle}</div>}
        {content}
      </ATooltip>
    </>
  );
};

export default Tooltip;

const {anchorRef, children, ...ATooltipProps} = ATooltip.propTypes;

Tooltip.propTypes = {
  /**
   * All props from ATooltip.
   */
  ...ATooltipProps,
  /**
   * Children must be a single element (not any node), to be able to attach
   * event listeners.
   */
  children: PropTypes.element,
  /**
   * Content of the tooltip.
   */
  content: PropTypes.node,
  /**
   * Delay for opening  the tooltip in ms.
   *
   * @defaultValue 400
   */
  openDelay: PropTypes.number,
  /**
   * Option to set the tooltip position
   *
   * @defaultValue top-left
   */
  placement: PropTypes.string,
  /** title can be a string value or component **/
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** subtitle can be a string value or component **/
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * Delay for closing the tooltip in ms. If not provided, calculated as openDelay / 2.
   */
  closeDelay: PropTypes.number
};
